import { Member } from '../types';

/**
 * Backend OrganizationRole hierarchy: OWNER > ADMIN > BILLING > MANAGER > MEMBER > VIEWER.
 *
 * BILLING is admin-equivalent for invoicing/payment flows, but NOT for resource-management
 * flows (PTO, onboarding, offboarding, kudos) — there the backend bypass threshold is ADMIN
 * (`@securityService.hasRoleInCurrentOrganization('ADMIN')`), which excludes BILLING. Keep the
 * two thresholds separate so the UI matches what the backend actually authorizes.
 */
const ADMIN_ROLES = ['OWNER', 'ADMIN', 'BILLING'] as const;
const RESOURCE_ADMIN_ROLES = ['OWNER', 'ADMIN'] as const;
const MANAGER_ROLE = 'MANAGER';

/** String-based helper: true when the raw role string is an admin role (billing-inclusive). */
export const isAdminRole = (role?: string | null): boolean =>
  !!role && ADMIN_ROLES.includes(role as (typeof ADMIN_ROLES)[number]);

/**
 * String-based helper for resource-management flows: true only for OWNER/ADMIN.
 * Mirrors the backend ADMIN bypass threshold used by PTO and similar flows (excludes BILLING).
 */
export const isResourceAdminRole = (role?: string | null): boolean =>
  !!role && RESOURCE_ADMIN_ROLES.includes(role as (typeof RESOURCE_ADMIN_ROLES)[number]);

/** String-based helper: true when the raw role string is the manager role. */
export const isManagerRole = (role?: string | null): boolean =>
  role === MANAGER_ROLE;

export const isAdmin = (user: Member | null | undefined): boolean =>
  isAdminRole(user?.organizationRole);

export const isResourceAdmin = (user: Member | null | undefined): boolean =>
  isResourceAdminRole(user?.organizationRole);

export const isManager = (user: Member | null | undefined): boolean =>
  isManagerRole(user?.organizationRole);

export const isManagerOrAbove = (user: Member | null | undefined): boolean =>
  isAdmin(user) || isManager(user);
